import React, { ReactNode, isValidElement } from 'react';
import IconButton, { IconButtonProps } from '../../../IconButton/IconButton';
import { Button, ButtonProps } from '../../../Button/Button';
import { Icon, IconProps } from '../../../Icon/Icon';

/**
 * Check if a ReactNode is an IconButton component.
 * Used by passthroughChildrenWithDisabledState to detect interactive buttons
 * and propagate disabled state. Returns true only for actual IconButton instances.
 * Handles both direct imports and forwarded components via function name matching.
 */
export function isIconButtonElement(
  node: ReactNode,
): node is React.ReactElement<IconButtonProps> {
  return (
    isValidElement(node) &&
    (node.type === IconButton ||
      (typeof node.type === 'function' && node.type.name === 'IconButton'))
  );
}

/**
 * Check if a ReactNode is a Button component.
 * Used to detect regular buttons in input slots and propagate disabled state.
 * Handles both direct imports and forwarded components via function name matching.
 */
export function isButtonElement(
  node: ReactNode,
): node is React.ReactElement<ButtonProps> {
  return (
    isValidElement(node) &&
    (node.type === Button ||
      (typeof node.type === 'function' && node.type.name === 'Button'))
  );
}

/**
 * Check if a ReactNode is an Icon component.
 * Used to detect icons in input slots and apply disabled color styling.
 * When the Input is disabled, icons automatically become 'disabledPrimary' color.
 * Handles both direct imports and forwarded components via function name matching.
 */
export function isIconElement(
  node: ReactNode,
): node is React.ReactElement<IconProps> {
  return (
    isValidElement(node) &&
    (node.type === Icon ||
      (typeof node.type === 'function' && node.type.name === 'Icon'))
  );
}
